/**
 * Hook to enrich experiences with media for stay experiences
 */

import { useState, useEffect, useMemo } from 'react';
import { ExperienceCard } from '@/constants/chatScreenData';
import { ChatExperience } from '@/types/chatTypes';
import { fetchMultipleStayExperienceMedia } from '@/utils/fetchStayExperienceMedia';
import { mapChatExperiencesToCards } from '@/components/chat/conversations/utils/experienceMapper';

/**
 * Hook to enrich ChatExperiences with media before mapping to ExperienceCard
 * @param rawExperiences - Raw ChatExperience array from API
 * @returns Enriched and mapped ExperienceCard array
 */
export function useEnrichedExperiences(
  rawExperiences: ChatExperience[] | undefined | null
): ExperienceCard[] {
  const [mediaMap, setMediaMap] = useState<
    Map<string, Array<{ url: string; [key: string]: unknown }>>
  >(new Map());

  // Identify stay experiences that need media fetching
  const stayExperienceIds = useMemo(() => {
    if (!rawExperiences) return [];
    return rawExperiences
      .filter(exp => {
        const expType = exp.experienceType?.toLowerCase() || '';
        const isStayExperience =
          expType.includes('stay') || expType === 'stayexperience';
        // Only fetch if no media and no images
        const hasNoImages =
          (!exp.media || exp.media.length === 0) &&
          (!exp.imageUrl || exp.imageUrl.length === 0) &&
          (!exp.image || exp.image.length === 0);
        return isStayExperience && hasNoImages && (exp.id || exp._id);
      })
      .map(exp => exp.id || exp._id || '')
      .filter((id): id is string => id !== '');
  }, [rawExperiences]);

  // Create a stable dependency string for the effect
  const stayExperienceIdsKey = useMemo(
    () => stayExperienceIds.sort().join(','),
    [stayExperienceIds]
  );

  // Fetch media for stay experiences
  useEffect(() => {
    if (stayExperienceIds.length === 0) return;

    let isMounted = true;

    const fetchMedia = async () => {
      try {
        const fetchedMedia =
          await fetchMultipleStayExperienceMedia(stayExperienceIds);

        if (isMounted) {
          setMediaMap(fetchedMedia);
        }
      } catch (error) {
        console.warn('Failed to fetch media for stay experiences:', error);
      }
    };

    fetchMedia();

    return () => {
      isMounted = false;
    };
  }, [stayExperienceIdsKey, stayExperienceIds]);

  // Enrich experiences with fetched media before mapping
  const enrichedExperiences = useMemo(() => {
    if (!rawExperiences) return null;

    return rawExperiences.map(exp => {
      const expType = exp.experienceType?.toLowerCase() || '';
      const isStayExperience =
        expType.includes('stay') || expType === 'stayexperience';
      const expId = exp.id || exp._id || '';

      // If it's a stay experience and we fetched media for it, add it to the experience
      if (isStayExperience && expId && mediaMap.has(expId)) {
        const mediaItems = mediaMap.get(expId) || [];
        // Map fetched media items to the format expected by the mapper
        const media = mediaItems.map(item => ({
          url: item.url,
          format: 'image',
          isActive: true,
          ...item,
        }));

        // Replace existing media with fetched media (since we only fetch when there's no media/images)
        return {
          ...exp,
          media: media,
        };
      }

      return exp;
    });
  }, [rawExperiences, mediaMap]);

  // Map enriched experiences to ExperienceCard format
  return useMemo(() => {
    if (!enrichedExperiences) return [];
    return mapChatExperiencesToCards(enrichedExperiences);
  }, [enrichedExperiences]);
}
